import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Headphones, Heart, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const ONBOARDING_KEY = 'omas_onboarding_done';

const OnboardingModal = () => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(() => !localStorage.getItem(ONBOARDING_KEY));
  const { t } = useLanguage();

  const steps = [
    { icon: BookOpen, color: 'bg-gold/20 text-gold', title: t('onboarding.step1.title'), desc: t('onboarding.step1.desc') },
    { icon: Headphones, color: 'bg-old-rose/20 text-old-rose', title: t('onboarding.step2.title'), desc: t('onboarding.step2.desc') },
    { icon: Heart, color: 'bg-night-blue/15 text-night-blue', title: t('onboarding.step3.title'), desc: t('onboarding.step3.desc') },
  ];

  const dismiss = () => { localStorage.setItem(ONBOARDING_KEY, 'true'); setVisible(false); };
  const next = () => { if (step < 2) setStep(step + 1); else dismiss(); };

  if (!visible) return null;

  const current = steps[step];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-card rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-border">
          <div className="relative p-8 text-center">
            <button onClick={dismiss} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">
              <X className="w-5 h-5" />
            </button>

            <motion.div key={step} initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
              <div className={`w-20 h-20 rounded-2xl ${current.color} flex items-center justify-center mx-auto mb-6`}>
                <current.icon className="w-10 h-10" />
              </div>
              <h2 className="font-display text-3xl text-foreground mb-3">{current.title}</h2>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-sm mx-auto">{current.desc}</p>
            </motion.div>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-6">
              {steps.map((_, i) => (
                <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${i === step ? 'bg-gold w-8' : 'bg-muted'}`} />
              ))}
            </div>

            <div className="flex gap-3 justify-center">
              {step < 2 && (
                <Button variant="ghost" onClick={dismiss} className="rounded-full" data-testid="onboarding-skip-btn">
                  {t('onboarding.skip')}
                </Button>
              )}
              <Button onClick={next} className="rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold px-8" data-testid="onboarding-next-btn">
                {step < 2 ? t('onboarding.next') : t('onboarding.start')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingModal;
